import { useState, useEffect, useRef } from 'react';
import CommentSection from './CommentSection';
import LinkPreview from './LinkPreview';
import ContextualAdSlot from './ContextualAdSlot';
import { fetchOgData, reportPost } from '../services/api';
import { getApiBase } from '../services/runtimeConfig';

const API_BASE = getApiBase();

const URL_REGEX = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/i;
let activePreviewVideo = null;

const REPORT_REASONS = [
  { value: 'false_information', label: 'False Information' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'pornography', label: 'Pornography' },
  { value: 'hate_speech', label: 'Hate Speech' },
  { value: 'violence', label: 'Violence' },
  { value: 'other', label: 'Other' },
];

function extractFirstUrl(text) {
  const m = URL_REGEX.exec(text);
  if (!m) return null;
  const raw = m[0].replace(/[.,!?)]$/, '');
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

function isVideoUrl(url) {
  return /\.(mp4|webm|mov)$/i.test(url || '');
}

function getYouTubeId(url) {
  try {
    const u = new URL(url);
    if (/youtube\.com/.test(u.hostname)) return u.searchParams.get('v');
    if (/youtu\.be/.test(u.hostname)) return u.pathname.slice(1).split('?')[0] || null;
  } catch {}
  return null;
}

function getVimeoId(url) {
  try {
    const u = new URL(url);
    if (/vimeo\.com/.test(u.hostname)) {
      const m = u.pathname.match(/\/(\d+)/);
      return m ? m[1] : null;
    }
  } catch {}
  return null;
}

function normalizeMedia(media = []) {
  return media
    .map((item, index) => {
      if (typeof item === 'string') {
        return {
          url: item,
          order: index,
          originalIndex: index,
        };
      }

      if (item && typeof item === 'object') {
        const url = item.url || item.path || '';
        const order = Number.isFinite(item.order)
          ? item.order
          : Number.isFinite(item.position)
            ? item.position
            : index;

        return {
          url,
          order,
          originalIndex: index,
        };
      }

      return {
        url: '',
        order: index,
        originalIndex: index,
      };
    })
    .filter((item) => item.url)
    .sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.originalIndex - b.originalIndex;
    });
}

function getCollagePlacement(index, total) {
  if (total === 3) {
    if (index === 0) return { gridColumn: '1', gridRow: '1 / 3' };
    if (index === 1) return { gridColumn: '2', gridRow: '1' };
    if (index === 2) return { gridColumn: '2', gridRow: '2' };
  }

  if (total >= 5) {
    if (index === 0) return { gridColumn: '1', gridRow: '1 / 3' };
    if (index === 1) return { gridColumn: '2', gridRow: '1' };
    if (index === 2) return { gridColumn: '2', gridRow: '2' };
    if (index === 3) return { gridColumn: '1', gridRow: '3' };
    if (index === 4) return { gridColumn: '2', gridRow: '3' };
  }

  return null;
}

function MediaItem({ url, onOpen, videosMuted, onToggleVideosMuted }) {
  const fullUrl = `${API_BASE}${url}`;
  const isVideo = isVideoUrl(url);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!videoRef.current) return;
    videoRef.current.muted = videosMuted;
  }, [videosMuted]);

  useEffect(() => {
    if (!isVideo || !videoRef.current) return;

    const videoEl = videoRef.current;
    let shouldPlay = false;

    function syncPlayback() {
      if (!videoEl) return;

      if (shouldPlay) {
        if (activePreviewVideo && activePreviewVideo !== videoEl) {
          activePreviewVideo.pause();
        }
        activePreviewVideo = videoEl;
        videoEl.play().catch(() => {});
      } else {
        videoEl.pause();
        if (activePreviewVideo === videoEl) {
          activePreviewVideo = null;
        }
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry || !videoEl) return;

        // Keep playback in sync with viewport visibility while user scrolls the feed.
        shouldPlay = entry.isIntersecting && entry.intersectionRatio >= 0.2;
        syncPlayback();
      },
      { threshold: [0, 0.2, 0.5, 0.75] }
    );

    function handleCanPlay() {
      if (shouldPlay) {
        syncPlayback();
      }
    }

    videoEl.addEventListener('loadeddata', handleCanPlay);
    videoEl.addEventListener('canplay', handleCanPlay);
    observer.observe(videoEl);

    return () => {
      videoEl.removeEventListener('loadeddata', handleCanPlay);
      videoEl.removeEventListener('canplay', handleCanPlay);
      observer.disconnect();
      if (activePreviewVideo === videoEl) {
        activePreviewVideo = null;
      }
      videoEl.pause();
    };
  }, [isVideo]);

  function toggleInlineSound() {
    const nextMuted = !videosMuted;
    onToggleVideosMuted(nextMuted);

    if (videoRef.current) {
      videoRef.current.muted = nextMuted;
      if (!nextMuted) {
        videoRef.current.play().catch(() => {});
      }
    }
  }

  return (
    isVideo ? (
      <div className="media-video-preview-wrap">
        <button type="button" className="media-open-btn" onClick={onOpen} title="Open media">
          <video
            ref={videoRef}
            className="media-item media-thumb-preview"
            preload="metadata"
            muted={videosMuted}
            autoPlay
            loop
            playsInline
          >
            <source src={fullUrl} />
          </video>
        </button>
        <button type="button" className="media-inline-sound-btn" onClick={toggleInlineSound}>
          {videosMuted ? 'Unmute' : 'Mute'}
        </button>
        <span className="video-thumb-overlay">Tap video to open</span>
      </div>
    ) : (
      <button type="button" className="media-open-btn" onClick={onOpen} title="Open media">
        <img
          className="media-item"
          src={fullUrl}
          alt="Post attachment"
          loading="lazy"
        />
      </button>
    )
  );
}

function CompactMediaTile({ url, onOpen, overflowLabel = '' }) {
  const fullUrl = `${API_BASE}${url}`;
  const isVideo = isVideoUrl(url);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!isVideo || !videoRef.current) return;
    const videoEl = videoRef.current;
    // Imperatively set muted — React's muted prop doesn't set the DOM property
    videoEl.muted = true;

    let shouldPlay = false;

    function syncPlayback() {
      if (shouldPlay) {
        if (activePreviewVideo && activePreviewVideo !== videoEl) {
          activePreviewVideo.pause();
        }
        activePreviewVideo = videoEl;
        videoEl.play().catch(() => {});
      } else {
        videoEl.pause();
        if (activePreviewVideo === videoEl) activePreviewVideo = null;
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        shouldPlay = entry.isIntersecting && entry.intersectionRatio >= 0.2;
        syncPlayback();
      },
      { threshold: [0, 0.2, 0.5, 0.75] }
    );

    function handleCanPlay() {
      if (shouldPlay) syncPlayback();
    }

    videoEl.addEventListener('loadeddata', handleCanPlay);
    videoEl.addEventListener('canplay', handleCanPlay);
    observer.observe(videoEl);

    return () => {
      videoEl.removeEventListener('loadeddata', handleCanPlay);
      videoEl.removeEventListener('canplay', handleCanPlay);
      observer.disconnect();
      if (activePreviewVideo === videoEl) activePreviewVideo = null;
      videoEl.pause();
    };
  }, [isVideo]);

  return (
    <button type="button" className="media-collage-btn" onClick={onOpen} title="Open media">
      {isVideo ? (
        <video ref={videoRef} className="media-collage-thumb" muted playsInline preload="auto" loop>
          <source src={fullUrl} />
        </video>
      ) : (
        <img className="media-collage-thumb" src={fullUrl} alt="Post attachment" loading="lazy" />
      )}

      {isVideo && <span className="media-collage-video-label">Video</span>}
      {overflowLabel && <span className="media-collage-overflow">{overflowLabel}</span>}
    </button>
  );
}

export default function PostCard({
  post,
  selectedCountry = '',
  feedVideosMuted = true,
  onFeedVideosMutedChange = () => {},
}) {
  const postId = post._id || post.id;
  const date = new Date(post.timestamp);
  const relative = formatRelative(date);
  const [ogData, setOgData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [activeMediaIndex, setActiveMediaIndex] = useState(-1);
  const [videoMuted, setVideoMuted] = useState(true);
  const [reportReason, setReportReason] = useState('false_information');
  const [reporting, setReporting] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [showReportToast, setShowReportToast] = useState(false);

  const linkUrl = extractFirstUrl(post.content);
  const orderedMedia = normalizeMedia(post.media);
  const previewMedia = orderedMedia.slice(0, 4);
  const photoCount = orderedMedia.filter((item) => !isVideoUrl(item.url)).length;
  const mediaCount = orderedMedia.length;
  const showPictureCount = photoCount > 4;
  const showBottomOverflow = mediaCount > 4;
  const activeMedia = activeMediaIndex >= 0 ? orderedMedia[activeMediaIndex] : null;
  const lightboxTrackRef = useRef(null);

  useEffect(() => {
    if (activeMedia && isVideoUrl(activeMedia.url)) {
      setVideoMuted(true);
    }
  }, [activeMedia]);

  useEffect(() => {
    if (activeMediaIndex < 0 || !lightboxTrackRef.current) return;
    const track = lightboxTrackRef.current;
    const target = track.children[activeMediaIndex];
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }
  }, [activeMediaIndex]);

  useEffect(() => {
    if (!linkUrl) {
      setOgData(null);
      return;
    }
    let cancelled = false;
    fetchOgData(linkUrl)
      .then((data) => { if (!cancelled) setOgData(data); })
      .catch(() => { if (!cancelled) setOgData(null); });
    return () => { cancelled = true; };
  }, [linkUrl]);

  async function copyShareLink(shareUrl) {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch (_) {
      /* non-fatal */
    }
  }

  async function handleShare() {
    const shareUrl = `${window.location.origin}/share/${postId}`;
    const shareTitle = `${post.username} on TheScandal`;
    const shareText = post.content?.slice(0, 200) || 'Interesting post on TheScandal';

    try {
      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        return;
      }
      await copyShareLink(shareUrl);
    } catch (_) {
      /* non-fatal */
    }
  }

  async function handleReport() {
    if (reporting) return;
    setReporting(true);
    try {
      await reportPost(postId, reportReason);
      setReportModalOpen(false);
      setShowReportToast(true);
      setTimeout(() => setShowReportToast(false), 2000);
    } catch (err) {
      alert(err.message || 'Failed to submit report');
    } finally {
      setReporting(false);
    }
  }

  function openReportModal() {
    setReportModalOpen(true);
  }

  function closeReportModal() {
    if (reporting) return;
    setReportModalOpen(false);
  }

  function handleCardClick(event) {
    if (event.target.closest('button, a, input, textarea, select, label, video')) return;
    setExpanded(true);
  }

  function openMedia(index) {
    setActiveMediaIndex(index);
  }

  function closeMedia() {
    setActiveMediaIndex(-1);
  }

  function toggleVideoSound() {
    setVideoMuted((prev) => !prev);
  }

  function handleLightboxScroll(event) {
    const track = event.currentTarget;
    if (!track) return;
    const slideWidth = track.clientWidth;
    if (!slideWidth) return;
    const nextIndex = Math.round(track.scrollLeft / slideWidth);
    if (nextIndex !== activeMediaIndex) {
      setActiveMediaIndex(nextIndex);
    }
  }

  function showPreviousMedia() {
    if (activeMediaIndex <= 0) return;
    setActiveMediaIndex((prev) => Math.max(0, prev - 1));
  }

  function showNextMedia() {
    if (activeMediaIndex >= mediaCount - 1) return;
    setActiveMediaIndex((prev) => Math.min(mediaCount - 1, prev + 1));
  }

  return (
    <article
      id={`post-${postId}`}
      className={`post-card post-card-clickable${post.flagged ? ' post-flagged' : ''}${expanded ? ' post-card-expanded' : ''}`}
      onClick={handleCardClick}
      title="Tap post to open comments"
    >
      <div className="post-meta">
        <div className="post-meta-primary">
          <span className="post-username">{post.username}</span>
          <span className="post-time" title={date.toLocaleString()}>
            {relative}
          </span>
          {post.flagged && <span className="flag-badge">⚑ Flagged</span>}
        </div>
        {(post.country || post.placeMentioned) && (
          <div className="post-meta-secondary">
            {post.country && (
              <span className="location-badge" title="Country">
                {post.country}
              </span>
            )}
            {post.placeMentioned && (
              <span className="location-badge" title="Place mentioned">
                {post.placeMentioned}
              </span>
            )}
          </div>
        )}
      </div>

      <p className="post-content">{post.content}</p>

      {linkUrl && (() => {
        const ytId = getYouTubeId(linkUrl);
        if (ytId) {
          return (
            <div className="content-video-embed">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${ytId}`}
                title="YouTube video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          );
        }
        const vimeoId = getVimeoId(linkUrl);
        if (vimeoId) {
          return (
            <div className="content-video-embed">
              <iframe
                src={`https://player.vimeo.com/video/${vimeoId}`}
                title="Vimeo video"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          );
        }
        if (isVideoUrl(linkUrl)) {
          return (
            <video
              className="content-video-inline"
              src={linkUrl}
              controls
              muted
              playsInline
            />
          );
        }
        return <LinkPreview og={ogData} url={linkUrl} />;
      })()}

      {mediaCount > 0 && (
        <section className="post-media-section" aria-label="Post media">
          <div className="post-media-meta">
            {showPictureCount && (
              <span className="post-media-count" title={`${photoCount} photos uploaded`}>
                {photoCount}+
              </span>
            )}
            {!expanded && mediaCount > 1 && (
              <span className="post-media-hint">Tap any photo to view</span>
            )}
          </div>

          {expanded ? (
            <div className="media-grid">
              {orderedMedia.map((item, i) => (
                <MediaItem
                  key={`${item.url}-${i}`}
                  url={item.url}
                  onOpen={() => openMedia(i)}
                  videosMuted={feedVideosMuted}
                  onToggleVideosMuted={onFeedVideosMutedChange}
                />
              ))}
            </div>
          ) : (
            <div className={`media-collage media-collage-count-${previewMedia.length}`}>
              {previewMedia.map((item, i) => {
                const placement = getCollagePlacement(i, previewMedia.length);
                const overflowLabel = showBottomOverflow && i === previewMedia.length - 1 ? '4+' : '';

                return (
                  <div
                    key={`${item.url}-${i}`}
                    className="media-collage-cell"
                    style={placement || undefined}
                  >
                    <CompactMediaTile
                      url={item.url}
                      onOpen={() => openMedia(i)}
                      overflowLabel={overflowLabel}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      <div className="post-actions">
        <button
          className="btn-ghost btn-sm"
          onClick={handleShare}
          title="Share post to apps"
        >
          Share
        </button>
        <button
          className="btn-ghost btn-sm btn-report"
          onClick={openReportModal}
          title="Report this post"
        >
          Report
        </button>
      </div>

      <ContextualAdSlot postId={postId} text={post.content || ''} adType="banner" />

      <CommentSection
        postId={postId}
        initialCount={post.commentsCount || 0}
        selectedCountry={selectedCountry || post.country || ''}
        forceOpen={expanded}
      />

      {activeMedia && (
        <div className="media-lightbox" role="dialog" aria-modal="true" onClick={closeMedia}>
          <div className="media-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="media-lightbox-close" onClick={closeMedia} aria-label="Close media viewer">✕</button>
            {mediaCount > 1 && (
              <>
                <button
                  type="button"
                  className="media-lightbox-nav media-lightbox-prev"
                  onClick={showPreviousMedia}
                  aria-label="View previous media"
                  disabled={activeMediaIndex <= 0}
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="media-lightbox-nav media-lightbox-next"
                  onClick={showNextMedia}
                  aria-label="View next media"
                  disabled={activeMediaIndex >= mediaCount - 1}
                >
                  ›
                </button>
              </>
            )}
            <div className="media-lightbox-header">
              <span>{activeMediaIndex + 1} / {mediaCount}</span>
            </div>
            <div
              className="media-lightbox-track"
              ref={lightboxTrackRef}
              onScroll={handleLightboxScroll}
            >
              {orderedMedia.map((item, i) => {
                const isVideo = isVideoUrl(item.url);
                const itemUrl = `${API_BASE}${item.url}`;
                const isActive = i === activeMediaIndex;

                return (
                  <div key={`${item.url}-${i}`} className="media-lightbox-slide">
                    {isVideo ? (
                      <div className="media-lightbox-video-wrap">
                        <video
                          className="media-lightbox-video"
                          src={itemUrl}
                          controls
                          autoPlay={isActive}
                          muted={videoMuted}
                          playsInline
                        />
                        {isActive && (
                          <button type="button" className="btn-ghost btn-sm media-sound-toggle" onClick={toggleVideoSound}>
                            {videoMuted ? 'Enable sound' : 'Mute sound'}
                          </button>
                        )}
                      </div>
                    ) : (
                      <img className="media-lightbox-image" src={itemUrl} alt="Post attachment" />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="media-lightbox-hint">
              Swipe, scroll, or use arrows to view more media
            </div>
          </div>
        </div>
      )}

      {reportModalOpen && (
        <div className="report-modal-backdrop" role="dialog" aria-modal="true" onClick={closeReportModal}>
          <div className="report-modal" onClick={(event) => event.stopPropagation()}>
            <h3>Report Post</h3>
            <p>Select a reason for this report:</p>

            <label className="sr-only" htmlFor={`reportReason-${postId}`}>
              Report reason
            </label>
            <select
              id={`reportReason-${postId}`}
              className="report-reason-select"
              value={reportReason}
              onChange={(event) => setReportReason(event.target.value)}
            >
              {REPORT_REASONS.map((reason) => (
                <option key={reason.value} value={reason.value}>{reason.label}</option>
              ))}
            </select>

            <div className="report-modal-actions">
              <button type="button" className="btn-ghost btn-sm" onClick={closeReportModal} disabled={reporting}>
                Cancel
              </button>
              <button type="button" className="btn-ghost btn-sm btn-report" onClick={handleReport} disabled={reporting}>
                {reporting ? 'Reporting...' : 'Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReportToast && (
        <div className="report-toast" role="status" aria-live="polite">
          Thank you, report submitted.
        </div>
      )}
    </article>
  );
}

function formatRelative(date) {
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}
